import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Copy, ChevronDown, ChevronRight, ArrowLeft, Building2, Layers, Package, Upload, FileSpreadsheet, Check, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const MEASURE_UNITS = [
  { value: "m2", label: "m\u00B2" },
  { value: "pcs", label: "pcs" },
  { value: "linear_m", label: "lin.m" },
  { value: "kg", label: "kg" },
];

function measureLabel(uom) {
  return MEASURE_UNITS.find(m => m.value === uom)?.label || uom;
}

export default function JobMaterialsPage({ user, onLogout }) {
  const { t } = useTranslation();
  const { projectId, jobId } = useParams();
  const navigate = useNavigate();

  const [job, setJob] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [units, setUnits] = useState([]);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [expandedBuildings, setExpandedBuildings] = useState({});
  const [expandedSubtasks, setExpandedSubtasks] = useState({});
  const [projectName, setProjectName] = useState("");
  const [uploadDialog, setUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [applyingExcel, setApplyingExcel] = useState(false);

  const tracking = job?.tracking_level || "per_unit";
  const canEdit = user.role !== "contractor" && user.role !== "accountant";
  const subtasks = job?.subtasks || [];
  const hasSubtasks = subtasks.length > 0;

  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const loadData = useCallback(async () => {
    if (!projectId || !jobId) return;
    try {
      const [jobRes, bldgRes, unitRes, projRes] = await Promise.all([
        axios.get(`/job-materials/${jobId}`),
        axios.get(`/buildings?project_id=${projectId}`),
        axios.get(`/units?project_id=${projectId}`),
        axios.get(`/projects`),
      ]);
      setJob(jobRes.data.job);
      setBuildings(bldgRes.data);
      const apartmentsOnly = unitRes.data.filter(u => !u.unit_type || u.unit_type === "residential" || u.unit_type === "commercial");
      setUnits(apartmentsOnly);

      const proj = projRes.data.find(p => p.id === projectId);
      setProjectName(proj?.name || "");

      // Only set expand states on first load
      if (!initialLoadDone) {
        const exp = {};
        const stList = jobRes.data.job?.subtasks || [];
        const stIds = stList.length > 0 ? stList.map(s => s.id) : ["_none"];
        bldgRes.data.forEach(b => {
          stIds.forEach(stId => { exp[`${stId}-${b.id}`] = true; });
        });
        setExpandedBuildings(exp);

        // All subtasks collapsed by default
        const stExp = {};
        stExp["_none"] = true; // only for jobs without subtasks
        setExpandedSubtasks(stExp);
        setInitialLoadDone(true);
      }

      // Pre-populate editData with subtask_id in key
      const ed = {};
      jobRes.data.items.forEach(item => {
        const stId = item.subtask_id || "_none";
        const unitKey = item.unit_id ? `unit_${item.unit_id}` : `floor_${item.building_id}_${item.floor}`;
        const key = `${stId}__${unitKey}`;
        ed[key] = { quantity: item.quantity, unit_of_measure: item.unit_of_measure };
      });
      setEditData(ed);
    } catch (e) {
      console.error("Failed to load", e);
      toast.error("Failed to load data");
    }
  }, [projectId, jobId, initialLoadDone]);

  useEffect(() => { loadData(); }, [loadData]);

  const updateField = (key, field, value) => {
    setEditData(prev => ({
      ...prev,
      [key]: { ...(prev[key] || { quantity: 0, unit_of_measure: job?.measurement_unit || "m2" }), [field]: value }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const items = [];
      const subtaskList = hasSubtasks ? subtasks : [{ id: null }];

      for (const st of subtaskList) {
        const stId = st.id || "_none";
        if (tracking === "per_floor") {
          buildings.forEach(b => {
            const bUnits = units.filter(u => u.building_id === b.id);
            const floors = [...new Set(bUnits.map(u => u.floor))].sort((a, b) => a - b);
            floors.forEach(fl => {
              const key = `${stId}__floor_${b.id}_${fl}`;
              const d = editData[key];
              if (d && d.quantity) {
                items.push({ floor: fl, building_id: b.id, subtask_id: st.id, quantity: parseFloat(d.quantity) || 0, unit_of_measure: d.unit_of_measure });
              }
            });
          });
        } else {
          units.forEach(u => {
            const key = `${stId}__unit_${u.id}`;
            const d = editData[key];
            if (d && d.quantity) {
              items.push({ unit_id: u.id, building_id: u.building_id, floor: u.floor, subtask_id: st.id, quantity: parseFloat(d.quantity) || 0, unit_of_measure: d.unit_of_measure });
            }
          });
        }
      }

      const res = await axios.post(`/job-materials/${jobId}/bulk`, { items });
      toast.success(`Saved: ${res.data.created} created, ${res.data.updated} updated`);
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const handleApplyFloor = async (buildingId, sourceFloor, subtaskId) => {
    const bUnits = units.filter(u => u.building_id === buildingId);
    const stId = subtaskId || "_none";

    if (tracking === "per_floor") {
      const key = `${stId}__floor_${buildingId}_${sourceFloor}`;
      const d = editData[key];
      if (!d || !d.quantity || parseFloat(d.quantity) === 0) {
        toast.error(`Enter a quantity for Floor ${sourceFloor} first`);
        return;
      }
    } else {
      const floorUnits = bUnits.filter(u => u.floor === sourceFloor);
      const hasData = floorUnits.some(u => {
        const key = `${stId}__unit_${u.id}`;
        const d = editData[key];
        return d && d.quantity && parseFloat(d.quantity) > 0;
      });
      if (!hasData) {
        toast.error(`Enter quantities for Floor ${sourceFloor} apartments first`);
        return;
      }
    }

    setApplying(true);
    try {
      // Save first, then apply
      await handleSave();
      await axios.post(`/job-materials/${jobId}/apply-floor`, {
        source_floor: sourceFloor,
        source_building_id: buildingId,
        subtask_id: subtaskId || null,
      });
      toast.success("Copied to all floors");
      await loadData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to apply");
    } finally { setApplying(false); }
  };

  const toggleBuilding = (bId) => {
    setExpandedBuildings(prev => ({ ...prev, [bId]: !prev[bId] }));
  };

  const toggleSubtask = (stId) => {
    setExpandedSubtasks(prev => ({ ...prev, [stId]: !prev[stId] }));
  };

  // Calculate subtask total across all units
  const getSubtaskTotal = (stId) => {
    let total = 0;
    Object.entries(editData).forEach(([key, val]) => {
      if (key.startsWith(`${stId}__`) && val.quantity) {
        total += parseFloat(val.quantity) || 0;
      }
    });
    return Math.round(total * 100) / 100;
  };

  const getSubtaskUom = (stId) => {
    for (const [key, val] of Object.entries(editData)) {
      if (key.startsWith(`${stId}__`) && val.unit_of_measure) {
        return val.unit_of_measure;
      }
    }
    return job?.measurement_unit || "m2";
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadPreview(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`/job-materials/${jobId}/upload-excel`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadPreview(res.data);
      setUploadDialog(true);
      toast.success(`Parsed ${res.data.total_items} items from ${res.data.sections_found} sections`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to parse Excel");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleApplyExcel = async () => {
    if (!uploadPreview?.preview) return;
    setApplyingExcel(true);
    try {
      const res = await axios.post(`/job-materials/${jobId}/apply-excel`, { items: uploadPreview.preview });
      toast.success(`Applied: ${res.data.created} created, ${res.data.updated} updated`);
      setUploadDialog(false);
      setUploadPreview(null);
      await loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to apply");
    } finally { setApplyingExcel(false); }
  };

  if (!job) return (
    <Layout user={user} onLogout={onLogout}>
      <div className="text-center py-20 text-slate-400">{t("jobMaterials.loading")}</div>
    </Layout>
  );

  const subtaskList = hasSubtasks ? subtasks : [{ id: null, name: null }];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-4" data-testid="job-materials-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" className="rounded-sm" onClick={() => navigate(`/project-progress`)} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <p className="text-[11px] text-slate-400 uppercase tracking-wider">{projectName}</p>
              <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900" data-testid="job-name">
                {job.job_name} — {t("jobMaterials.materialQuantities")}
              </h1>
              <div className="flex items-center gap-3 mt-1">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-medium ${
                  tracking === "per_floor" ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-sky-50 text-sky-700 border border-sky-200"
                }`}>
                  {tracking === "per_floor" ? <><Layers className="w-3 h-3"/> {t("jobMaterials.perFloor")}</> : <><Building2 className="w-3 h-3"/> {t("jobMaterials.perApartment")}</>}
                </span>
                {hasSubtasks && (
                  <span className="text-xs text-slate-500">{subtasks.length} {t("jobMaterials.subtasks")}</span>
                )}
              </div>
            </div>
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <label className="cursor-pointer">
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleExcelUpload} disabled={uploading} />
                <div className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-sm border transition-colors ${
                  uploading ? "bg-slate-100 text-slate-400 border-slate-200" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50 hover:border-slate-400"
                }`} data-testid="upload-excel-btn">
                  <FileSpreadsheet className="w-4 h-4" />
                  {uploading ? t("jobMaterials.parsing") : t("jobMaterials.uploadExcel")}
                </div>
              </label>
              <Button onClick={handleSave} disabled={saving} className="rounded-sm bg-indigo-600 hover:bg-indigo-700" data-testid="save-materials-btn">
                <Save className="w-4 h-4 mr-1" /> {saving ? t("jobMaterials.saving") : t("jobMaterials.saveAll")}
              </Button>
            </div>
          )}
        </div>

        {/* Subtask sections */}
        {subtaskList.map(st => {
          const stId = st.id || "_none";
          const stTotal = getSubtaskTotal(stId);
          const defaultUom = job.measurement_unit || "m2";

          return (
            <div key={stId} className="space-y-2">
              {/* Subtask header (only if job has subtasks) */}
              {hasSubtasks && (
                <button
                  onClick={() => toggleSubtask(stId)}
                  className="flex items-center gap-2 w-full text-left px-3 py-2 bg-indigo-50 border border-indigo-200 rounded-sm hover:bg-indigo-100 transition-colors"
                  data-testid={`subtask-header-${stId}`}
                >
                  {expandedSubtasks[stId] ? <ChevronDown className="w-4 h-4 text-indigo-400"/> : <ChevronRight className="w-4 h-4 text-indigo-400"/>}
                  <span className="text-sm font-bold text-indigo-900">{st.name}</span>
                  {st.weight > 0 && <span className="text-[11px] text-indigo-500 bg-indigo-100 px-1.5 py-0.5 rounded">{st.weight}%</span>}
                  {stTotal > 0 && (
                    <span className="ml-auto text-xs font-semibold text-indigo-700">
                      {t("jobMaterials.total")}: {stTotal.toLocaleString()} {measureLabel(getSubtaskUom(stId))}
                    </span>
                  )}
                </button>
              )}

              {/* Building grids (shown if subtask is expanded or no subtasks) */}
              {(expandedSubtasks[stId] || !hasSubtasks) && buildings.map(b => {
                const bUnits = units.filter(u => u.building_id === b.id).sort((a, c) => {
                  if (a.floor !== c.floor) return a.floor - c.floor;
                  return (a.code || "").localeCompare(c.code || "");
                });
                if (bUnits.length === 0) return null;

                const floors = [...new Set(bUnits.map(u => u.floor))].sort((a, c) => a - c);
                const firstFloor = floors[0];

                return (
                  <div key={`${stId}-${b.id}`} className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                    <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-200">
                      <button onClick={() => toggleBuilding(`${stId}-${b.id}`)}
                        className="flex items-center gap-2 text-left hover:text-slate-900 transition-colors">
                        {expandedBuildings[`${stId}-${b.id}`] !== false ? <ChevronDown className="w-4 h-4 text-slate-400"/> : <ChevronRight className="w-4 h-4 text-slate-400"/>}
                        <span className="text-sm font-semibold text-slate-800">{b.name}</span>
                        <span className="text-[11px] text-slate-400">{bUnits.length} {t("jobMaterials.units")}, {floors.length} {t("jobMaterials.floors")}</span>
                      </button>
                      {canEdit && floors.length > 1 && (
                        <Button variant="outline" size="sm" className="rounded-sm text-xs h-7"
                          onClick={() => handleApplyFloor(b.id, firstFloor, st.id)}
                          disabled={applying}
                          data-testid={`apply-floor-btn-${stId}-${b.id}`}>
                          <Copy className="w-3 h-3 mr-1" />
                          {applying ? t("jobMaterials.applying") : t("jobMaterials.copyFloorToAll", { floor: firstFloor })}
                        </Button>
                      )}
                    </div>

                    {expandedBuildings[`${stId}-${b.id}`] !== false && (
                      <div>
                        {tracking === "per_floor" ? (
                          <>
                            <div className="grid grid-cols-[120px_1fr_140px_140px] gap-0 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase tracking-wider">
                              <div className="px-3 py-2">{t("jobMaterials.floor")}</div>
                              <div className="px-3 py-2">{t("jobMaterials.unitsOnFloor")}</div>
                              <div className="px-3 py-2">{t("jobMaterials.quantity")}</div>
                              <div className="px-3 py-2">{t("jobMaterials.unit")}</div>
                            </div>
                            {floors.map(fl => {
                              const key = `${stId}__floor_${b.id}_${fl}`;
                              const d = editData[key] || { quantity: "", unit_of_measure: defaultUom };
                              const floorUnits = bUnits.filter(u => u.floor === fl);
                              return (
                                <div key={fl} className="grid grid-cols-[120px_1fr_140px_140px] gap-0 border-b border-slate-50 items-center hover:bg-slate-50/50">
                                  <div className="px-3 py-2 text-sm font-semibold text-slate-800">{t("jobMaterials.floor")} {fl}</div>
                                  <div className="px-3 py-2 text-xs text-slate-500">{floorUnits.map(u => u.code).join(", ")}</div>
                                  <div className="px-1.5 py-1">
                                    {canEdit ? (
                                      <Input type="number" step="0.01" className="h-7 text-sm rounded-sm" value={d.quantity}
                                        onChange={e => updateField(key, "quantity", e.target.value)} />
                                    ) : (
                                      <span className="text-sm px-3">{d.quantity || "\u2014"}</span>
                                    )}
                                  </div>
                                  <div className="px-1.5 py-1">
                                    {canEdit ? (
                                      <Select value={d.unit_of_measure} onValueChange={v => updateField(key, "unit_of_measure", v)}>
                                        <SelectTrigger className="h-7 rounded-sm text-sm"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                          {MEASURE_UNITS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                                        </SelectContent>
                                      </Select>
                                    ) : (
                                      <span className="text-sm px-3">{measureLabel(d.unit_of_measure)}</span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </>
                        ) : (
                          <>
                            <div className="grid grid-cols-[160px_80px_80px_140px_140px] gap-0 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase tracking-wider">
                              <div className="px-3 py-2">{t("jobMaterials.unit")}</div>
                              <div className="px-3 py-2">{t("jobMaterials.floor")}</div>
                              <div className="px-3 py-2">Net m²</div>
                              <div className="px-3 py-2">{t("jobMaterials.quantity")}</div>
                              <div className="px-3 py-2">{t("jobMaterials.unit")}</div>
                            </div>
                            {floors.map(fl => {
                              const floorUnits = bUnits.filter(u => u.floor === fl);
                              return (
                                <div key={fl}>
                                  <div className="bg-slate-50/50 px-3 py-1 text-[11px] font-semibold text-slate-500 border-b border-slate-100">
                                    {t("jobMaterials.floor")} {fl} — {floorUnits.length} {t("jobMaterials.units")}
                                  </div>
                                  {floorUnits.map(u => {
                                    const key = `${stId}__unit_${u.id}`;
                                    const d = editData[key] || { quantity: "", unit_of_measure: defaultUom };
                                    return (
                                      <div key={u.id} className="grid grid-cols-[160px_80px_80px_140px_140px] gap-0 border-b border-slate-50 items-center hover:bg-slate-50/50"
                                        data-testid={`unit-row-${stId}-${u.id}`}>
                                        <div className="px-3 py-1.5 text-sm font-medium text-slate-800">{u.code}</div>
                                        <div className="px-3 py-1.5 text-xs text-slate-500">F{u.floor}</div>
                                        <div className="px-3 py-1.5 text-xs text-slate-600">{u.net_m2 || "\u2014"}</div>
                                        <div className="px-1.5 py-1">
                                          {canEdit ? (
                                            <Input type="number" step="0.01" className="h-7 text-sm rounded-sm" value={d.quantity}
                                              onChange={e => updateField(key, "quantity", e.target.value)}
                                              data-testid={`qty-${stId}-${u.id}`} />
                                          ) : (
                                            <span className="text-sm px-3">{d.quantity || "\u2014"}</span>
                                          )}
                                        </div>
                                        <div className="px-1.5 py-1">
                                          {canEdit ? (
                                            <Select value={d.unit_of_measure} onValueChange={v => updateField(key, "unit_of_measure", v)}>
                                              <SelectTrigger className="h-7 rounded-sm text-sm"><SelectValue /></SelectTrigger>
                                              <SelectContent>
                                                {MEASURE_UNITS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                                              </SelectContent>
                                            </Select>
                                          ) : (
                                            <span className="text-sm px-3">{measureLabel(d.unit_of_measure)}</span>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                  {/* Floor total for this subtask */}
                                  {(() => {
                                    let floorTotal = 0;
                                    floorUnits.forEach(u => {
                                      const d = editData[`${stId}__unit_${u.id}`];
                                      if (d && d.quantity) floorTotal += parseFloat(d.quantity) || 0;
                                    });
                                    return floorTotal > 0 ? (
                                      <div className="grid grid-cols-[160px_80px_80px_140px_140px] gap-0 border-b border-slate-200 bg-slate-50/80">
                                        <div className="px-3 py-1 text-[11px] font-bold text-slate-600 col-span-3 text-right">{t("jobMaterials.floor")} {fl} {t("jobMaterials.total").toLowerCase()}:</div>
                                        <div className="px-3 py-1 text-[11px] font-bold text-indigo-700">{floorTotal.toLocaleString(undefined, {maximumFractionDigits: 1})} {measureLabel(defaultUom)}</div>
                                        <div></div>
                                      </div>
                                    ) : null;
                                  })()}
                                </div>
                              );
                            })}
                          </>
                        )}
                        {/* Building total for this subtask */}
                        {(() => {
                          let bTotal = 0;
                          if (tracking === "per_floor") {
                            floors.forEach(fl => {
                              const d = editData[`${stId}__floor_${b.id}_${fl}`];
                              if (d && d.quantity) bTotal += parseFloat(d.quantity) || 0;
                            });
                          } else {
                            bUnits.forEach(u => {
                              const d = editData[`${stId}__unit_${u.id}`];
                              if (d && d.quantity) bTotal += parseFloat(d.quantity) || 0;
                            });
                          }
                          return bTotal > 0 ? (
                            <div className="px-4 py-2 bg-indigo-50 border-t border-indigo-200 flex items-center justify-between">
                              <span className="text-xs font-bold text-indigo-800">{b.name} — {t("jobMaterials.total")}</span>
                              <span className="text-sm font-bold text-indigo-700">{bTotal.toLocaleString(undefined, {maximumFractionDigits: 1})} {measureLabel(defaultUom)}</span>
                            </div>
                          ) : null;
                        })()}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}

        {buildings.length === 0 && (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            <Package className="w-12 h-12 mb-3 text-slate-200" />
            <p className="text-sm">{t("jobMaterials.noBuildings")}</p>
          </div>
        )}

        {/* Excel Upload Preview Dialog */}
        <Dialog open={uploadDialog} onOpenChange={setUploadDialog}>
          <DialogContent className="sm:max-w-2xl w-[calc(100%-2rem)] max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                {t("jobMaterials.excelImportPreview")}
              </DialogTitle>
            </DialogHeader>

            {uploadPreview && (
              <div className="space-y-4">
                <div className="flex items-center gap-4 text-sm">
                  <span className="bg-emerald-50 text-emerald-700 px-2 py-1 rounded text-xs font-semibold">
                    {uploadPreview.total_items} {t("jobMaterials.itemsParsed")}
                  </span>
                  <span className="text-slate-500">
                    {uploadPreview.sections_found} {t("jobMaterials.sectionsFound")}, {uploadPreview.buildings_matched} {t("jobMaterials.buildingsMatched")}
                  </span>
                </div>

                {/* Preview grouped by subtask */}
                {(() => {
                  const groups = {};
                  (uploadPreview.preview || []).forEach(item => {
                    const key = item.subtask_name || t("jobMaterials.unmapped");
                    if (!groups[key]) groups[key] = { items: [], total: 0, uom: item.unit_of_measure };
                    groups[key].items.push(item);
                    groups[key].total += item.quantity;
                  });
                  return Object.entries(groups).map(([name, group]) => (
                    <div key={name} className="border border-slate-200 rounded-sm overflow-hidden">
                      <div className="bg-slate-50 px-3 py-2 flex items-center justify-between border-b border-slate-200">
                        <span className="text-sm font-semibold text-slate-800">{name}</span>
                        <span className="text-xs font-bold text-indigo-700">
                          {group.total.toFixed(1)} {measureLabel(group.uom)} ({group.items.length} {t("jobMaterials.entries")})
                        </span>
                      </div>
                      <div className="max-h-40 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50 sticky top-0">
                            <tr>
                              <th className="text-left px-3 py-1 text-slate-500">{t("jobMaterials.building")}</th>
                              <th className="text-left px-3 py-1 text-slate-500">{t("jobMaterials.unit")}</th>
                              <th className="text-right px-3 py-1 text-slate-500">{t("jobMaterials.qty")}</th>
                              <th className="text-left px-3 py-1 text-slate-500">{t("jobMaterials.unit")}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {group.items.map((item, i) => (
                              <tr key={i} className="border-t border-slate-50 hover:bg-slate-50/50">
                                <td className="px-3 py-1 text-slate-600">{item.building_name}</td>
                                <td className="px-3 py-1 font-medium text-slate-800">{item.unit_code}</td>
                                <td className="px-3 py-1 text-right font-semibold text-slate-900">{item.quantity}</td>
                                <td className="px-3 py-1 text-slate-500">{measureLabel(item.unit_of_measure)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}

            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setUploadDialog(false)} className="rounded-sm">
                <X className="w-4 h-4 mr-1" /> {t("common.cancel")}
              </Button>
              <Button onClick={handleApplyExcel} disabled={applyingExcel} className="rounded-sm bg-emerald-600 hover:bg-emerald-700" data-testid="apply-excel-btn">
                <Check className="w-4 h-4 mr-1" /> {applyingExcel ? t("jobMaterials.applying") : t("jobMaterials.applyItems", { count: uploadPreview?.total_items || 0 })}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
